import { useState, useRef, useEffect, useCallback } from "react";
import { cn } from "@/lib/utils";

interface Win2kMenuItemProps {
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
}

export function Win2kMenuItem({ onClick, children, disabled }: Win2kMenuItemProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "w-full text-left px-4 py-0.5 text-xs cursor-default",
        disabled
          ? "text-[var(--win2k-shadow)]"
          : "hover:bg-[var(--win2k-title-blue-start)] hover:text-white"
      )}
    >
      {children}
    </button>
  );
}

interface Win2kMenuProps {
  label: string;
  children: React.ReactNode;
}

export function Win2kMenu({ label, children }: Win2kMenuProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
      setOpen(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open, handleClickOutside]);

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "px-1 cursor-default text-xs",
          open
            ? "bg-[var(--win2k-title-blue-start)] text-white"
            : "hover:underline"
        )}
      >
        {label}
      </button>
      {open && (
        <div
          className="absolute left-0 top-full z-50 min-w-[160px] py-0.5 bg-[var(--win2k-window-bg)] win2k-outset"
          onClick={() => setOpen(false)}
        >
          {children}
        </div>
      )}
    </div>
  );
}
